/**
 * KML export utility for flight plans.
 * Generates KML XML and triggers a browser download.
 */

/**
 * Generate KML content from polygon and waypoints.
 * @param {Object} params
 * @param {string} params.name - Flight plan name
 * @param {{ lat: number, lng: number }[]} params.polygonPoints - Polygon vertices
 * @param {{ latitude: number, longitude: number }[]} params.waypoints - Flight waypoints
 * @param {number} params.flightHeight - Altitude for waypoints
 * @returns {string} KML XML string
 */
export const generateKML = ({ name, polygonPoints, waypoints, flightHeight = 80 }) => {
	const coords = polygonPoints.map(p => `${p.lng},${p.lat},0`).join(' ');
	const waypointsKml = waypoints
		.map(
			(wp, i) => `
		<Placemark>
			<name>Waypoint ${i + 1}</name>
			<Point><coordinates>${wp.longitude},${wp.latitude},${flightHeight}</coordinates></Point>
		</Placemark>`
		)
		.join('');

	return `<?xml version="1.0" encoding="UTF-8"?>
			<kml xmlns="https://www.opengis.net/kml/2.2">
				<Document>
					<name>${name || 'Flight Plan'}</name>
					<Placemark>
						<name>Flight Area</name>
						<Polygon>
							<outerBoundaryIs>
								<LinearRing>
									<coordinates>${coords} ${polygonPoints[0].lng},${polygonPoints[0].lat},0</coordinates>
								</LinearRing>
							</outerBoundaryIs>
						</Polygon>
					</Placemark>
					${waypointsKml}
				</Document>
			</kml>`;
};

/**
 * Download a KML file to the user's device.
 * @param {Object} params
 * @param {string} params.name - File name (without extension)
 * @param {{ lat: number, lng: number }[]} params.polygonPoints - Polygon vertices
 * @param {{ latitude: number, longitude: number }[]} params.waypoints - Flight waypoints
 * @param {number} params.flightHeight - Altitude for waypoints
 */
export const downloadKML = ({ name, polygonPoints, waypoints, flightHeight = 80 }) => {
	const kml = generateKML({ name, polygonPoints, waypoints, flightHeight });
	const blob = new Blob([kml], { type: 'application/vnd.google-earth.kml+xml' });
	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = `${name || 'flight-plan'}.kml`;
	a.click();
	URL.revokeObjectURL(url);
};
